import {
  AlertTriangle,
  CheckCircle2,
  Play,
  Save,
  Send,
  Webhook,
  X,
} from "lucide-react";
import * as React from "react";

import { workflowsApi } from "@/api/workflows";
import { Canvas } from "@/components/workflow/Canvas";
import { NodePalette } from "@/components/workflow/NodePalette";
import { PropertiesPanel } from "@/components/workflow/PropertiesPanel";
import { TestRun } from "@/components/workflow/TestRun";
import { TriggersPanel } from "@/components/workflow/TriggersPanel";
import { ValidationErrors } from "@/components/workflow/ValidationErrors";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LoadingPage } from "@/components/ui/loading-page";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { useRouteStore } from "@/stores/route";
import { useUIStore } from "@/stores/ui";
import {
  flowToGraph,
  useWorkflowDesignerStore,
} from "@/stores/workflowDesigner";

interface Props {
  code: string;
}

type SidePanel = "validate" | "test" | "triggers" | null;

export function WorkflowDesigner({ code }: Props): JSX.Element {
  const navigate = useRouteStore((s) => s.navigate);
  const pushToast = useUIStore((s) => s.pushToast);

  const loadGraph = useWorkflowDesignerStore((s) => s.loadGraph);
  const isDirty = useWorkflowDesignerStore((s) => s.isDirty);
  const versionInfo = useWorkflowDesignerStore((s) => s.versionInfo);
  const validationErrors = useWorkflowDesignerStore((s) => s.validationErrors);
  const setValidationErrors = useWorkflowDesignerStore((s) => s.setValidationErrors);
  const workflowName = useWorkflowDesignerStore((s) => s.workflowName);

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [sidePanel, setSidePanel] = React.useState<SidePanel>(null);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const versions = await workflowsApi.listVersions(code);
      // Pick latest draft, fallback latest published
      const draft = versions.find((v) => v.status === "draft");
      const target = draft ?? versions.find((v) => v.status === "published") ?? versions[0];
      if (!target) {
        pushToast({
          variant: "warning",
          title: `Workflow ${code} chưa có version`,
        });
        setLoading(false);
        return;
      }
      const detail = await workflowsApi.getVersion(code, target.version);
      loadGraph(detail.graph, {
        code: detail.code,
        name: detail.name,
        description: detail.description ?? "",
        version: detail.version,
        status: detail.status,
      });
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lỗi tải workflow",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  }, [code, loadGraph, pushToast]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleSave = async (): Promise<void> => {
    const state = useWorkflowDesignerStore.getState();
    const graph = flowToGraph(state);
    setSaving(true);
    try {
      const v = await workflowsApi.saveDraft(code, { graph });
      pushToast({
        variant: "success",
        title: `Đã save draft v${v.version}`,
      });
      await refresh();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lưu thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async (): Promise<void> => {
    const state = useWorkflowDesignerStore.getState();
    const graph = flowToGraph(state);
    try {
      const res = await workflowsApi.validate(code, { graph });
      setValidationErrors(res.errors);
      setSidePanel("validate");
      if (res.is_valid) {
        pushToast({ variant: "success", title: "Validation OK" });
      }
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Validate thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  const handlePublish = async (): Promise<void> => {
    if (!versionInfo) return;
    if (isDirty) {
      pushToast({
        variant: "warning",
        title: "Save Draft trước khi publish",
      });
      return;
    }
    if (!confirm(`Publish workflow ${code} v${versionInfo.version}?`)) return;
    setSaving(true);
    try {
      await workflowsApi.publish(code, versionInfo.version);
      pushToast({
        variant: "success",
        title: `Đã publish v${versionInfo.version}`,
      });
      await refresh();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Publish thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  // Keyboard shortcuts (P2.1-S5)
  useKeyboardShortcuts(
    {
      "cmd+s": () => {
        if (!saving) void handleSave();
      },
      del: () => {
        const state = useWorkflowDesignerStore.getState();
        if (state.selectedNodeId) state.removeNode(state.selectedNodeId);
      },
    },
    { enabled: !loading },
  );

  if (loading) {
    return <LoadingPage label="Đang tải workflow..." />;
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      <header className="h-12 shrink-0 border-b flex items-center px-3 gap-2 bg-background">
        <button
          onClick={() => navigate("admin.workflows")}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          ← Workflows
        </button>
        <div className="flex items-center gap-2 ml-2 min-w-0">
          <span className="font-semibold text-sm truncate">{workflowName}</span>
          <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{code}</code>
          {versionInfo ? (
            <Badge
              variant={versionInfo.status === "published" ? "default" : "outline"}
              className="text-[10px]"
            >
              v{versionInfo.version} {versionInfo.status}
            </Badge>
          ) : null}
          {isDirty ? (
            <Badge variant="secondary" className="text-[10px]">
              · unsaved
            </Badge>
          ) : null}
        </div>
        <div className="ml-auto flex items-center gap-1">
          <Button
            size="sm"
            variant={sidePanel === "validate" ? "default" : "outline"}
            onClick={() => void handleValidate()}
          >
            <CheckCircle2 className="size-4 mr-1" /> Validate
          </Button>
          <Button
            size="sm"
            variant={sidePanel === "test" ? "default" : "outline"}
            onClick={() => setSidePanel(sidePanel === "test" ? null : "test")}
          >
            <Play className="size-4 mr-1" /> Test Run
          </Button>
          <Button
            size="sm"
            variant={sidePanel === "triggers" ? "default" : "outline"}
            onClick={() =>
              setSidePanel(sidePanel === "triggers" ? null : "triggers")
            }
            title="Webhook + Cron triggers"
          >
            <Webhook className="size-4 mr-1" /> Triggers
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void handleSave()}
            disabled={saving || !isDirty}
          >
            <Save className="size-4 mr-1" />
            {saving ? "..." : "Save Draft"}
          </Button>
          <Button
            size="sm"
            onClick={() => void handlePublish()}
            disabled={saving}
          >
            <Send className="size-4 mr-1" /> Publish
          </Button>
        </div>
      </header>

      <div className="flex-1 flex min-h-0">
        <NodePalette />
        <div className="flex-1 flex flex-col min-h-0">
          {validationErrors.length > 0 ? (
            <button
              type="button"
              onClick={() => setSidePanel("validate")}
              className="shrink-0 flex items-center gap-2 bg-rose-50 dark:bg-rose-950/40 border-b border-rose-300 dark:border-rose-800 px-3 py-1.5 text-xs text-rose-900 dark:text-rose-100 hover:bg-rose-100 dark:hover:bg-rose-950/60 transition-colors"
              title="Xem chi tiết errors"
            >
              <AlertTriangle className="size-3.5" />
              <span className="font-medium">
                {validationErrors.length} validation error
                {validationErrors.length > 1 ? "s" : ""}
              </span>
              <span className="ml-auto opacity-70">click để mở panel →</span>
            </button>
          ) : null}
          <Canvas />
        </div>
        {sidePanel === "validate" ? (
          <SidePane title="Validation" onClose={() => setSidePanel(null)}>
            <div className="p-3">
              <ValidationErrors errors={validationErrors} />
            </div>
          </SidePane>
        ) : sidePanel === "test" ? (
          <SidePane title="Test Run" onClose={() => setSidePanel(null)}>
            <TestRun />
          </SidePane>
        ) : sidePanel === "triggers" ? (
          <TriggersPanel
            workflowCode={code}
            onClose={() => setSidePanel(null)}
          />
        ) : null}
        <PropertiesPanel />
      </div>
    </div>
  );
}

function SidePane({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}): JSX.Element {
  return (
    <aside className="w-96 shrink-0 border-l bg-background overflow-y-auto">
      <div className="px-3 py-2 border-b flex items-center justify-between sticky top-0 bg-background z-10">
        <h2 className="text-xs font-semibold uppercase">{title}</h2>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </div>
      {children}
    </aside>
  );
}
